import { Routes, Route, Navigate } from 'react-router-dom';
import { useApi } from './contexts/ApiContext';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import DomainsPage from './pages/DomainsPage';
import DomainDetailPage from './pages/DomainDetailPage';
import RegistrarsPage from './pages/RegistrarsPage';
import OnePasswordPage from './pages/OnePasswordPage';
import ApiSetupPage from './pages/ApiSetupPage';
import TagsPage from './pages/TagsPage';
import ImportPage from './pages/ImportPage';
import UsersPage from './pages/UsersPage';
import ApiKeysPage from './pages/ApiKeysPage';
import WhoisSearchPage from './pages/WhoisSearchPage';
import FootprintAuditPage from './pages/FootprintAuditPage';
import AuditLogPage from './pages/AuditLogPage';
import CronsPage from './pages/CronsPage';
import ManualDomainsPage from './pages/ManualDomainsPage';
import MindMapPage from './pages/MindMapPage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import RenewalReadinessPage from './pages/RenewalReadinessPage';
import AccountNamesPage from './pages/AccountNamesPage';
import RegistrarSyncPage from './pages/RegistrarSyncPage';

/**
 * The Registrars route serves two audiences: admins get the full management
 * page, while users holding only can_trigger_sync get a sync-only view. They
 * are separate components because the admin page loads admin-only endpoints
 * (accounts, types, 1Password status) that would 403 for a sync-only user.
 */
function RegistrarsOrSync() {
  const { isAdmin } = useApi();
  return isAdmin ? <RegistrarsPage /> : <RegistrarSyncPage />;
}

function ProtectedRoute({ children, adminOnly, superAdminOnly, mindMapOnly, classifyOnly, whoisOnly, footprintOnly, syncOnly }: { children: React.ReactNode; adminOnly?: boolean; superAdminOnly?: boolean; mindMapOnly?: boolean; classifyOnly?: boolean; whoisOnly?: boolean; footprintOnly?: boolean; syncOnly?: boolean }) {
  const { isAuthenticated, isAdmin, isSuperAdmin, canSeeMindMap, canClassify, canSeeWhois, canSeeFootprintAudit, canTriggerSync } = useApi();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (classifyOnly && !canClassify) {
    return <Navigate to="/" replace />;
  }

  if (mindMapOnly && !canSeeMindMap) {
    return <Navigate to="/" replace />;
  }

  if (whoisOnly && !canSeeWhois) {
    return <Navigate to="/" replace />;
  }

  if (syncOnly && !canTriggerSync) {
    return <Navigate to="/" replace />;
  }

  if (footprintOnly && !canSeeFootprintAudit) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
}

export default function App() {
  const { isAuthenticated } = useApi();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains"
        element={
          <ProtectedRoute>
            <DomainsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains/manual"
        element={
          <ProtectedRoute adminOnly>
            <ManualDomainsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/domains/:id"
        element={
          <ProtectedRoute>
            <DomainDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/registrars"
        element={
          <ProtectedRoute syncOnly>
            <RegistrarsOrSync />
          </ProtectedRoute>
        }
      />
      <Route
        path="/1password"
        element={
          <ProtectedRoute adminOnly>
            <OnePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-setup"
        element={
          <ProtectedRoute adminOnly>
            <ApiSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tags"
        element={
          <ProtectedRoute classifyOnly>
            <TagsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <ProtectedRoute adminOnly>
            <ImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute adminOnly>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute adminOnly>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whois"
        element={
          <ProtectedRoute whoisOnly>
            <WhoisSearchPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/footprint-audit"
        element={
          <ProtectedRoute footprintOnly>
            <FootprintAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mind-map"
        element={
          <ProtectedRoute mindMapOnly>
            <MindMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-log"
        element={
          <ProtectedRoute superAdminOnly>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crons"
        element={
          <ProtectedRoute superAdminOnly>
            <CronsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account-names"
        element={
          <ProtectedRoute adminOnly>
            <AccountNamesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/renewal-readiness"
        element={
          <ProtectedRoute adminOnly>
            <RenewalReadinessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute superAdminOnly>
            <IntegrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications/settings"
        element={
          <ProtectedRoute superAdminOnly>
            <NotificationSettingsPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
